import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { toast } from 'sonner';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { FiInfo, FiArrowLeft, FiUser, FiMail, FiLock, FiCheck, FiShield, FiCalendar, FiPieChart } from 'react-icons/fi';
import { HiOutlineOfficeBuilding, HiOutlineLightningBolt, HiOutlineSparkles, HiOutlineClock } from 'react-icons/hi';
import GoogleSignInButton from '../../components/auth/GoogleSignInButton.jsx';
import EmailVerification from '../../components/auth/EmailVerification.jsx';
import api from '../../services/api';
import invitationService from '../../services/invitationService';

const Register = () => {
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('token');
  const emailFromInvite = searchParams.get('email');
  const redirectUrl = searchParams.get('redirect');
  
  const [formData, setFormData] = useState({
    name: '',
    email: emailFromInvite || '',
    password: '',
    passwordConfirm: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [socialLoading, setSocialLoading] = useState({
    google: false
  });
  const [organizationDetection, setOrganizationDetection] = useState(null);
  const [checkingDomain, setCheckingDomain] = useState(false);
  const [joinOrganization, setJoinOrganization] = useState(false);
  const [invitationDetails, setInvitationDetails] = useState(null);
  const [validatingInvite, setValidatingInvite] = useState(false);
  const [verificationStep, setVerificationStep] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  
  const { register, sendVerificationCode, verifyAndRegister, resendVerificationCode } = useAuth();
  const navigate = useNavigate();

  const { name, email, password, passwordConfirm } = formData;

  // Validate invitation token if present
  useEffect(() => {
    const validateInvitationToken = async () => {
      if (invitationToken) {
        setValidatingInvite(true);
        try {
          const response = await invitationService.validateOrganizationInvitation(invitationToken);
          if (response.success) {
            setInvitationDetails(response.data.invitation);
            setFormData(prev => ({
              ...prev,
              email: response.data.invitation.email
            }));
            toast.info(`You're registering to join ${response.data.invitation.organization.name}`);
          }
        } catch (error) {
          console.error('Invalid invitation token:', error);
          toast.error('Invalid or expired invitation link');
        } finally {
          setValidatingInvite(false);
        }
      }
    };

    validateInvitationToken();
  }, [invitationToken]);

  // Check for organization domain when email changes
  useEffect(() => {
    const checkOrganizationDomain = async () => {
      if (email && email.includes('@')) {
        const domain = email.split('@')[1];
        if (domain && domain.length > 3) {
          setCheckingDomain(true);
          try {
            const response = await api.get(`/organizations/domain-check/${domain}`);
            const { available, organization } = response.data.data;
            
            if (!available && organization?.isActive) {
              setOrganizationDetection({
                exists: true,
                name: organization.name,
                domain: organization.domain
              });
            } else {
              setOrganizationDetection({ exists: false });
            }
          } catch (error) {
            setOrganizationDetection(null);
          }
          setCheckingDomain(false);
        }
      } else {
        setOrganizationDetection(null);
      }
    };

    const timeoutId = setTimeout(checkOrganizationDomain, 500);
    return () => clearTimeout(timeoutId);
  }, [email]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = () => {
    if (!name || !email || !password || !passwordConfirm) {
      toast.error('Please fill in all required fields');
      return false;
    }
    const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return false;
    }
    if (password !== passwordConfirm) {
      toast.error('Passwords do not match. Please check and try again');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const registrationData = {
        name, 
        email, 
        password,
        joinOrganization: joinOrganization && organizationDetection?.exists
      };
      
      // Step 1: Send verification code instead of directly registering
      await sendVerificationCode(registrationData);
      toast.success('Verification code sent to your email!');
      setVerificationStep(true);
      setVerificationError('');
    } catch (error) {
      const errorMessage = error.message || 'Registration failed. Please try again';
      if (errorMessage.includes('Too many')) {
        toast.error(errorMessage, { duration: 6000 });
      } else if (errorMessage.includes('Connection problem')) {
        toast.error(errorMessage, { duration: 5000 });
      } else if (errorMessage.includes('Server error')) {
        toast.error(errorMessage, { duration: 5000 });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle verification code submission
  const handleVerify = async (code) => {
    try {
      setVerificationLoading(true);
      setVerificationError('');
      
      const user = await verifyAndRegister({ email, code });
      
      // If there's an invitation token, accept it after registration
      if (invitationToken && invitationDetails) {
        try {
          const acceptResponse = await invitationService.acceptOrganizationInvitation(invitationToken);
          if (acceptResponse.success) {
            toast.success(`Welcome to ${invitationDetails.organization.name}! You've successfully joined the organization.`);
            try {
              const userResponse = await api.get('/auth/me');
              if (userResponse.data.success && userResponse.data.data) {
                localStorage.setItem('user', JSON.stringify(userResponse.data.data));
                await new Promise(resolve => setTimeout(resolve, 300));
              }
            } catch (reloadError) {
              console.error('Error reloading user data:', reloadError);
            }
            navigate('/org/dashboard', { replace: true });
            return;
          }
        } catch (inviteError) {
          console.error('Error accepting invitation:', inviteError);
          toast.warn('Account created but there was an issue accepting the invitation. You can accept it later.');
        }
      }
      
      if (user && organizationDetection?.exists && joinOrganization) {
        toast.success(`Welcome to ${organizationDetection.name}! You've been added to your organization.`);
        navigate('/org/dashboard');
      } else {
        toast.success('Account created successfully! Welcome to Tasklina');
        if (redirectUrl) {
          navigate(redirectUrl);
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      setVerificationError(error.message || 'Verification failed');
    } finally {
      setVerificationLoading(false);
    }
  };

  // Handle resend verification code
  const handleResendCode = async () => {
    await resendVerificationCode(email);
  };

  const getPasswordStrength = () => {
    if (!password) return { width: '0%', color: 'bg-gray-200', label: '', level: 0 };
    if (password.length < 6) return { width: '25%', color: 'bg-red-500', label: 'Weak', level: 1 };
    if (password.length < 10) return { width: '50%', color: 'bg-yellow-500', label: 'Fair', level: 2 };
    if (password.length < 14) return { width: '75%', color: 'bg-blue-500', label: 'Good', level: 3 };
    return { width: '100%', color: 'bg-green-500', label: 'Strong', level: 4 };
  };
  const passwordStrength = getPasswordStrength();

  const features = [
    { icon: FiCheck, text: 'Unlimited tasks and projects' },
    { icon: FiCalendar, text: 'Calendar sync with Google' },
    { icon: HiOutlineSparkles, text: 'AI-powered task suggestions' },
    { icon: FiPieChart, text: 'Personal productivity analytics' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-800 flex flex-col lg:flex-row">
      {/* Left Panel - Hero */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 p-12">
        <div className="flex flex-col justify-between w-full max-w-lg mx-auto">
          {/* Header */}
          <div>
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur">
                <img src="/icon.png" alt="Tasklina" className="w-8 h-8 object-contain" />
              </div>
              <span className="text-2xl font-bold text-white">Tasklina</span>
            </Link>
          </div>
          
          {/* Main Content */}
          <div className="space-y-8">
            <div>
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur text-white text-sm font-medium mb-6">
                <HiOutlineLightningBolt className="w-4 h-4 mr-2" />
                Start free, upgrade anytime
              </div>
              <h1 className="text-4xl font-bold text-white leading-tight mb-4">
                Create Your Personal Workspace
              </h1>
              <p className="text-lg text-emerald-100 leading-relaxed">
                Join thousands of individuals who use Tasklina to stay organized, 
                track their goals, and boost productivity.
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4">
              {features.map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <div key={idx} className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-white font-medium">{feature.text}</span>
                  </div>
                );
              })}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                <p className="text-2xl font-bold text-white">2 min</p>
                <p className="text-emerald-200 text-sm">Setup time</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                <p className="text-2xl font-bold text-white">Free</p>
                <p className="text-emerald-200 text-sm">To start</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                <p className="text-2xl font-bold text-white">100%</p>
                <p className="text-emerald-200 text-sm">Secure</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-emerald-200 text-sm">
            <p>Need a team workspace instead?</p>
            <Link to="/auth/register-organization" className="text-white font-semibold hover:underline">
              Create an Organization
            </Link>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-8 lg:px-12">
        <div className="w-full max-w-md">
          {/* Mobile Header */}
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center space-x-3 justify-center mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-700 flex items-center justify-center shadow-lg shadow-emerald-200 dark:shadow-emerald-900/50">
                <img src="/icon.png" alt="Tasklina" className="w-7 h-7 object-contain" />
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-white">Tasklina</span>
            </Link>
          </div>

          {verificationStep ? (
            /* Email Verification Step */
            <EmailVerification
              email={email}
              onVerify={handleVerify}
              onResend={handleResendCode}
              onBack={() => setVerificationStep(false)}
              loading={verificationLoading}
              error={verificationError}
            />
          ) : (
          <>
          {/* Form Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Create your account</h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1">Start your productivity journey</p>
              </div>
              <Link 
                to="/auth/register-options"
                className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
              >
                <FiArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Link>
            </div>

            {/* Invitation Details Alert */}
            {invitationDetails && (
              <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl mb-6">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <HiOutlineOfficeBuilding className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-indigo-900">Organization Invitation</h4>
                    <p className="text-sm text-indigo-700 mt-1">
                      <strong>{invitationDetails.invitedBy?.name}</strong> invited you to join{' '}
                      <strong>{invitationDetails.organization?.name}</strong> as{' '}
                      <span className="font-medium">
                        {invitationDetails.role === 'org_admin' ? 'Admin' : 
                         invitationDetails.role === 'dept_manager' ? 'Department Manager' : 
                         invitationDetails.role === 'team_lead' ? 'Team Lead' : 'Team Member'}
                      </span>
                    </p>
                    {invitationDetails.message && (
                      <p className="text-sm text-indigo-600 mt-2 italic">"{invitationDetails.message}"</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Organization Detection Alert */}
            {!invitationDetails && organizationDetection?.exists && (
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl mb-6">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <HiOutlineOfficeBuilding className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-blue-900">Organization Detected</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Your email domain <strong>({organizationDetection.domain})</strong> belongs to{' '}
                      <strong>{organizationDetection.name}</strong>
                    </p>
                    <p className="text-xs text-blue-600 mt-2 flex items-center">
                      <FiInfo className="w-3 h-3 mr-1" />
                      You'll be added automatically after signup
                    </p>
                  </div>
                </div>
              </div>
            )}

            {checkingDomain && email.includes('@') && (
              <div className="p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl mb-6">
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-emerald-600 border-t-transparent mr-3"></div>
                  Checking for organization...
                </div>
              </div>
            )}
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Full Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FiUser className="h-5 w-5 text-gray-400" />
                </div>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={name} 
                  onChange={handleChange} 
                  className="block w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all" 
                  placeholder="John Doe" 
                  required 
                />
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FiMail className="h-5 w-5 text-gray-400" />
                </div>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={email} 
                  onChange={handleChange} 
                  className={`block w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all ${invitationDetails ? 'bg-gray-50 dark:bg-gray-600 cursor-not-allowed' : ''}`}
                  placeholder="you@example.com" 
                  required 
                  readOnly={!!invitationDetails}
                  disabled={!!invitationDetails}
                />
              </div>
              {invitationDetails && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 flex items-center">
                  <FiLock className="w-3 h-3 mr-1" />
                  Email is locked to the invitation address
                </p>
              )}
            </div>

            {/* Password Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <FiLock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    type={showPassword ? "text" : "password"} 
                    id="password" 
                    name="password" 
                    value={password} 
                    onChange={handleChange} 
                    className="block w-full pl-12 pr-12 py-3 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all" 
                    placeholder="••••••••" 
                    required 
                    minLength="6" 
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="passwordConfirm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <FiLock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    type={showConfirmPassword ? "text" : "password"} 
                    id="passwordConfirm" 
                    name="passwordConfirm" 
                    value={passwordConfirm} 
                    onChange={handleChange} 
                    className="block w-full pl-12 pr-12 py-3 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all" 
                    placeholder="••••••••" 
                    required 
                    minLength="6" 
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Password Strength Indicator */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">Password strength</span>
                {passwordStrength.label && (
                  <span className={`text-xs font-medium ${
                    passwordStrength.level === 1 ? 'text-red-500' :
                    passwordStrength.level === 2 ? 'text-yellow-500' :
                    passwordStrength.level === 3 ? 'text-blue-500' : 'text-green-500'
                  }`}>
                    {passwordStrength.label}
                  </span>
                )}
              </div>
              <div className="h-1.5 w-full bg-gray-100 dark:bg-gray-600 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${passwordStrength.color} transition-all duration-300`} 
                  style={{ width: passwordStrength.width }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Use at least 6 characters. Longer passwords are stronger.</p>
            </div>

            {/* Join Organization Checkbox */}
            {organizationDetection?.exists && !invitationDetails && (
              <div className="flex items-start space-x-3 p-4 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
                <input
                  id="joinOrganization"
                  name="joinOrganization"
                  type="checkbox"
                  checked={joinOrganization}
                  onChange={(e) => setJoinOrganization(e.target.checked)}
                  className="h-5 w-5 text-emerald-600 focus:ring-emerald-500 border-gray-300 dark:border-gray-600 rounded mt-0.5 bg-white dark:bg-gray-600"
                />
                <div className="flex-1">
                  <label htmlFor="joinOrganization" className="text-sm font-medium text-gray-900 dark:text-white cursor-pointer">
                    Join my company workspace
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Auto-join {organizationDetection.name} based on your email domain
                  </p>
                </div>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                  Auto-detected
                </span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || validatingInvite}
              className="w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-lg shadow-emerald-200 dark:shadow-emerald-900/30 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading || validatingInvite ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-gray-600"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 bg-white dark:bg-gray-800 text-sm text-gray-500 dark:text-gray-400">Or continue with</span>
            </div>
          </div>

          {/* Social Login */}
          <div>
            <GoogleSignInButton 
              onLoading={(loading) => setSocialLoading({...socialLoading, google: loading})}
              fullWidth
            />
          </div>

          {/* Footer */}
          <p className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
            Already have an account?{' '}
            <Link to="/auth/login" className="font-semibold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300">
              Sign in
            </Link>
          </p>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;
